import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createTransfer } from '../../store/Slices/assetSlice';

const TransferForm = ({ userId, spotWalletId, futuresWalletId, aiWalletId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { spotBalances, futuresBalances, aiBalances, loading, error } = useSelector((state) => state.assets);
  const { light } = useSelector((state) => state.theme); // جلب حالة الثيم من Redux

  const [fromWallet, setFromWallet] = useState('');
  const [toWallet, setToWallet] = useState('');
  const [amount, setAmount] = useState('');
  const [availableBalance, setAvailableBalance] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const walletOptions = [
    { label: 'Spot Account', value: spotWalletId, type: 'Spot' },
    { label: 'Futures Account', value: futuresWalletId, type: 'Futures' },
    { label: 'AI Account', value: aiWalletId, type: 'AI' },
  ];

  // تحديد الرصيد المتاح بناءً على المحفظة المرسلة
  useEffect(() => {
    if (fromWallet) {
      const walletType = walletOptions.find((option) => option.value === fromWallet)?.type;
      let balanceData;
      if (walletType === 'Spot') balanceData = spotBalances;
      else if (walletType === 'Futures') balanceData = futuresBalances;
      else if (walletType === 'AI') balanceData = aiBalances;

      const usdtBalance = balanceData?.find((b) => b.asset_id?.symbol === 'USDT')?.balance || 0;
      setAvailableBalance(usdtBalance);
    } else {
      setAvailableBalance(0);
    }
  }, [fromWallet, spotBalances, futuresBalances, aiBalances]);

  // التحقق من الكمية
  useEffect(() => {
    if (amount && parseFloat(amount) > availableBalance) {
      setErrorMessage('Amount exceeds available USDT balance');
    } else if (fromWallet === toWallet && fromWallet) {
      setErrorMessage('Cannot transfer to the same wallet');
    } else {
      setErrorMessage('');
    }
  }, [amount, availableBalance, fromWallet, toWallet]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fromWallet || !toWallet || !amount || parseFloat(amount) <= 0) {
      setErrorMessage('Please fill all fields with valid values');
      return;
    }
    if (parseFloat(amount) > availableBalance) return;

    const transferData = {
      user_id: userId,
      from_wallet_id: fromWallet,
      to_wallet_id: toWallet,
      asset_id: spotBalances.find((b) => b.asset_id?.symbol === 'USDT')?.asset_id?._id,
      amount: parseFloat(amount),
    };

    dispatch(createTransfer(transferData)).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        setFromWallet('');
        setToWallet('');
        setAmount('');
      }
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        bgcolor: theme.palette.background.paper, // لون الخلفية من الثيم
        p: isMobile ? 2 : 3,
        borderRadius: 2,
        boxShadow: light ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.5)', // ظل يتغير بناءً على الثيم
        mb: 3,
      }}
    >
      <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: theme.palette.text.primary, mb: 2 }}>
        Transfer USDT
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel sx={{ color: theme.palette.text.secondary }}>From Wallet</InputLabel>
        <Select
          value={fromWallet}
          onChange={(e) => setFromWallet(e.target.value)}
          label="From Wallet"
          sx={{
            bgcolor: light ? '#E0E0E0' : '#2A2E37', // لون خلفية الـ Select
            color: theme.palette.text.primary,
            '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.brand.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.brand.main,
            },
          }}
        >
          {walletOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel sx={{ color: theme.palette.text.secondary }}>To Wallet</InputLabel>
        <Select
          value={toWallet}
          onChange={(e) => setToWallet(e.target.value)}
          label="To Wallet"
          sx={{
            bgcolor: light ? '#E0E0E0' : '#2A2E37',
            color: theme.palette.text.primary,
            '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.brand.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.brand.main,
            },
          }}
        >
          {walletOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Amount (USDT)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{
            bgcolor: light ? '#E0E0E0' : '#2A2E37',
            color: theme.palette.text.primary,
            '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
            '& .MuiInputBase-input': { color: theme.palette.text.primary },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.brand.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.brand.main,
            },
          }}
        />
        <Typography sx={{ color: theme.palette.text.secondary, mt: 1, fontSize: '0.9rem' }}>
          Available: {availableBalance.toFixed(2)} USDT
        </Typography>
      </Box>
      {errorMessage && (
        <Typography sx={{ color: 'red', mb: 2, fontSize: '0.9rem' }}>{errorMessage}</Typography>
      )}
      {error && (
        <Typography sx={{ color: 'red', mb: 2, fontSize: '0.9rem' }}>Error: {error}</Typography>
      )}
      <Button
        type="submit"
        variant="contained"
        disabled={loading || !!errorMessage}
        sx={{
          bgcolor: theme.palette.brand.main,
          color: light ? '#FFFFFF' : '#1A1C22', // لون النص يتغير ليكون مرئيًا على الخلفية الصفراء
          borderRadius: 2,
          transition: 'all 0.3s',
          '&:hover': {
            bgcolor: theme.palette.brand.main,
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
          },
          width: '100%',
          py: 1.5,
        }}
      >
        {loading ? 'Processing...' : 'Transfer'}
      </Button>
    </Box>
  );
};

export default TransferForm;